import styled, { css } from 'styled-components';
import { remCalc, colors, media } from '@prisjakt/ui-base';
import { TooltipPlacement, TooltipAlign } from './types';

const backgroundColor = colors.lightTheme.base600;
const textColor = colors.lightTheme.base0;
const arrowLength = 4;
const arrowAlignedOffset = 10;
const sharedVerticalCentering = css`
  top: 50%;
  transform: translateY(-50%);
`;
const sharedHorizontalCentering = css`
  left: 50%;
  transform: translateX(-50%);
`;

export const calcAlignedArrowPosition = (parentWidth: number | null) =>
  parentWidth !== null ? parentWidth / 2 - arrowLength / 2 : arrowAlignedOffset;

interface ITooltip {
  placement: TooltipPlacement;
  width?: number;
  align: TooltipAlign;
  parentWidth: number | null;
}

export const StyledTooltip = styled.div<ITooltip>`
  position: absolute;
  z-index: 9999;
  text-align: center;
  text-transform: none;
  pointer-events: none;
  font-size: ${remCalc(12)};
  font-weight: 700;
  line-height: 1.3em;
  border-radius: 3px;
  padding: 4px 5px;
  background-color: ${backgroundColor};
  color: ${textColor};
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: visibility 0s, opacity 0.2s linear;

  ${props =>
    props.width &&
    css`
      width: ${props.width}px;
      white-space: normal;
    `}

  ${media.smallOnly`
    display: none;
  `}

  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 0;
    height: 0;
    border: ${arrowLength}px solid transparent;
  }

  ${props =>
    props.placement === TooltipPlacement.BOTTOM &&
    css`
      top: calc(100% + ${arrowLength}px);

      &::before {
        border-bottom-color: ${backgroundColor};
        border-top-width: 0;
        top: -${arrowLength}px;
      }
    `}

  ${props =>
    props.placement === 'top' &&
    css`
      bottom: calc(100% + ${arrowLength}px);

      &::before {
        border-top-color: ${backgroundColor};
        border-bottom-width: 0;
        bottom: -${arrowLength}px;
      }
    `}

  ${props =>
    (props.placement === TooltipPlacement.TOP ||
      props.placement === TooltipPlacement.BOTTOM) &&
    props.align === TooltipAlign.CENTER &&
    css`
      ${sharedHorizontalCentering};

      &::before {
        ${sharedHorizontalCentering};
      }
    `}

  ${props =>
    (props.placement === TooltipPlacement.TOP ||
      props.placement === TooltipPlacement.BOTTOM) &&
    props.align === TooltipAlign.LEFT &&
    css`
      left: 0;

      &::before {
        left: ${calcAlignedArrowPosition(props.parentWidth)}px;
      }
    `}

  ${props =>
    (props.placement === TooltipPlacement.TOP ||
      props.placement === TooltipPlacement.BOTTOM) &&
    props.align === TooltipAlign.RIGHT &&
    css`
      right: 0;

      &::before {
        right: ${calcAlignedArrowPosition(props.parentWidth)}px;
      }
    `}

  ${props =>
    props.placement === TooltipPlacement.LEFT &&
    css`
      ${sharedVerticalCentering};
      right: calc(100% + ${arrowLength}px);

      &::before {
        ${sharedVerticalCentering};
        border-left-color: ${backgroundColor};
        border-right-width: 0;
        right: -${arrowLength}px;
      }
    `}

  ${props =>
    props.placement === TooltipPlacement.RIGHT &&
    css`
      ${sharedVerticalCentering};
      left: calc(100% + ${arrowLength}px);

      &::before {
        ${sharedVerticalCentering};
        border-right-color: ${backgroundColor};
        border-left-width: 0;
        left: -${arrowLength}px;
      }
    `}
`;

const sharedHoverStyles = css`
  opacity: 1;
  visibility: visible;
`;

interface TooltipWrapperProps {
  renderOnMouseOver: boolean;
  isHovering: boolean;
}

export const TooltipWrapper = styled.div<TooltipWrapperProps>`
  position: relative;
  flex: 1 0 auto;
  z-index: 2;

  ${props =>
    !props.renderOnMouseOver &&
    css`
      &:hover ${StyledTooltip} {
        ${sharedHoverStyles};
      }
    `}

  ${props =>
    props.isHovering &&
    css`
      ${StyledTooltip} {
        ${sharedHoverStyles};
      }
    `}
`;
